import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ROUTES } from '../../constants/routes';

interface RoleBasedRouteProps {
  children: React.ReactNode;
  allowedRoles: string[];
}

/**
 * Role-based route protection component
 * 
 * Checks if the authenticated user has one of the allowed roles
 * If not, redirects to the dashboard or shows access denied
 * 
 * @param children - The protected route content
 * @param allowedRoles - Array of role strings that are allowed to access this route
 * @returns The children if user has permission, otherwise redirects
 */
const RoleBasedRoute: React.FC<RoleBasedRouteProps> = ({ children, allowedRoles }) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    // Show loading indicator while checking authentication status
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />;
  }

  // If user doesn't have the required role, redirect to appropriate page based on context
  if (user && !allowedRoles.includes(user.role)) {
    // For dashboard or admin-specific routes, redirect to Advanced Delivery
    return <Navigate to={ROUTES.ADVANCED_DELIVERY} replace />;
  }

  // User has the required role, render the protected content
  return <>{children}</>;
};

export default RoleBasedRoute; 